import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Menu,
  Search,
  Bell,
  User,
  Settings,
  LogOut,
  ChevronDown
} from 'lucide-react';

const Header = ({ onMenuClick, isMobile }) => {
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const { admin, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const notifications = [
    { id: 1, type: 'success', message: 'New user registration completed', time: '2 min ago' },
    { id: 2, type: 'warning', message: 'Transaction requires review', time: '5 min ago' },
    { id: 3, type: 'info', message: 'System maintenance scheduled', time: '1 hour ago' }
  ];

  const getNotificationIcon = (type) => {
    const baseClasses = "w-2 h-2 rounded-full";
    switch (type) {
      case 'success': return `${baseClasses} bg-vetme-mint`;
      case 'warning': return `${baseClasses} bg-yellow-400`;
      case 'info': return `${baseClasses} bg-vetme-secondary`;
      default: return `${baseClasses} bg-vetme-gray-medium`;
    }
  };

  return (
    <header className="bg-white shadow-sm border-b border-vetme-gray-light">
      <div className="flex items-center justify-between px-4 sm:px-6 lg:px-8 h-16">
        {/* Left side - Logo and mobile menu */}
        <div className="flex items-center space-x-4">
          {/* Mobile menu button */}
          {isMobile && (
            <button
              onClick={onMenuClick}
              className="p-2 rounded-lg hover:bg-vetme-gray-lightest focus:outline-none focus:ring-2 focus:ring-vetme-mint"
              aria-label="Open menu"
            >
              <Menu className="w-6 h-6 text-vetme-dark" />
            </button>
          )}
          
          {/* VetMe Logo */}
          <div className="flex items-center space-x-3">
            {/* Logo Symbol */}
            <div className="w-8 h-8 bg-vetme-dark rounded-full flex items-center justify-center">
              <div className="w-4 h-4 relative">
                <div className="absolute inset-0 bg-white rounded-full opacity-20"></div>
                <div className="absolute top-1 left-1 w-2 h-2 bg-white rounded-full"></div>
              </div>
            </div>
            
            {/* Logo Text */}
            <span className="vetme-logo text-xl text-vetme-dark font-light">
              vetme
            </span>
            
            {/* Admin Badge */}
            <span className="hidden sm:inline-flex px-2 py-1 text-xs font-medium bg-vetme-mint-very-pale text-vetme-mint-dark rounded-full">
              Admin
            </span>
          </div>
        </div>

        {/* Center - Search (hidden on mobile) */}
        <div className="hidden md:flex flex-1 max-w-lg mx-8">
          <div className="relative w-full">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-vetme-gray-dark" />
            </div>
            <input
              type="text"
              placeholder="Search users, transactions..."
              className="input-vetme pl-10 w-full"
            />
          </div>
        </div>

        {/* Right side - Notifications and Profile */}
        <div className="flex items-center space-x-4">
          {/* Mobile search button */}
          <button className="md:hidden p-2 rounded-lg hover:bg-vetme-gray-lightest focus:outline-none focus:ring-2 focus:ring-vetme-mint">
            <Search className="w-5 h-5 text-vetme-gray-dark" />
          </button>

          {/* Notifications */}
          <div className="relative">
            <button
              onClick={() => setShowNotifications(!showNotifications)}
              className="relative p-2 rounded-lg hover:bg-vetme-gray-lightest focus:outline-none focus:ring-2 focus:ring-vetme-mint"
            >
              <Bell className="w-5 h-5 text-vetme-gray-dark" />
              {notifications.length > 0 && (
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
              )}
            </button>

            {/* Notifications dropdown */}
            {showNotifications && (
              <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-lg border border-vetme-gray-light z-50 animate-slide-down">
                <div className="p-4 border-b border-vetme-gray-light">
                  <h3 className="text-sm font-medium text-vetme-dark">Notifications</h3>
                </div>
                <div className="max-h-64 overflow-y-auto">
                  {notifications.map((notification) => (
                    <div key={notification.id} className="p-4 border-b border-vetme-gray-light last:border-b-0 hover:bg-vetme-gray-lightest">
                      <div className="flex items-start space-x-3">
                        <div className={getNotificationIcon(notification.type)} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-vetme-dark">{notification.message}</p>
                          <p className="text-xs text-vetme-gray-dark mt-1">{notification.time}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="p-3 border-t border-vetme-gray-light">
                  <button className="text-sm text-vetme-secondary hover:text-vetme-secondary-dark font-medium">
                    View all notifications
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Profile dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowProfileMenu(!showProfileMenu)}
              className="flex items-center space-x-3 p-2 rounded-lg hover:bg-vetme-gray-lightest focus:outline-none focus:ring-2 focus:ring-vetme-mint"
            >
              <div className="w-8 h-8 bg-vetme-secondary rounded-full flex items-center justify-center">
                <User className="w-4 h-4 text-white" />
              </div>
              <div className="hidden sm:block text-left">
                <div className="text-sm font-medium text-vetme-dark">
                  {admin ? `${admin.first_name} ${admin.last_name}` : 'Admin User'}
                </div>
                <div className="text-xs text-vetme-gray-dark">
                  {admin?.email || 'admin@vetmepay.com'}
                </div>
              </div>
              <ChevronDown className="hidden sm:block w-4 h-4 text-vetme-gray-dark" />
            </button>

            {/* Profile dropdown menu */}
            {showProfileMenu && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-vetme-gray-light z-50 animate-slide-down">
                <div className="py-2">
                  <a
                    href="#"
                    className="flex items-center px-4 py-2 text-sm text-vetme-dark hover:bg-vetme-gray-lightest"
                  >
                    <User className="w-4 h-4 mr-3" />
                    Profile
                  </a>
                  <a
                    href="#"
                    className="flex items-center px-4 py-2 text-sm text-vetme-dark hover:bg-vetme-gray-lightest"
                  >
                    <Settings className="w-4 h-4 mr-3" />
                    Settings
                  </a>
                  <hr className="my-2 border-vetme-gray-light" />
                  <button
                    onClick={handleLogout}
                    className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                  >
                    <LogOut className="w-4 h-4 mr-3" />
                    Sign out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile search bar (shown when search button is clicked) */}
      <div className="md:hidden border-t border-vetme-gray-light px-4 py-3">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-vetme-gray-dark" />
          </div>
          <input
            type="text"
            placeholder="Search users, transactions..."
            className="input-vetme pl-10 w-full"
          />
        </div>
      </div>
    </header>
  );
};

export default Header;